import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Popover } from '@abyss/web/ui/Popover';
import { Text } from '@abyss/web/ui/Text';
import { find } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLagoon } from '../../../../hooks/useLagoon';
import { ConstantsLagoon } from '../../../ConstantsLagoon';
import { h2SmallerFontForResponsive } from '../../../ConstantsStyles';
import { LinkWithTracking } from '../../../LinkWithTracking/LinkWithTracking';
import {
  networkAffiliationsCardsContainer,
  networkAffiliationsCardsContainerCollapsed,
  networkAffiliationsHeaderContainer,
  networkAffiliationsIconStyle,
  networkAffiliationsLinkContainer,
  networkAffiliationsLinkStyle,
  networkAffiliationsSectionContainer,
  networkAffiliationsTitleContainer,
  networkAffiliationsTitleStyle,
  networkAffiliationsViewAllDetailsLink,
} from './NetworkAffiliations.style';
import {
  NetworkAffiliation,
  NetworkAffiliationsCard,
} from './NetworkAffiliationsCard';

interface NetworkAffiliationsSectionProps {
  affiliations: NetworkAffiliation[];
  maxCardsToShow?: number;
  maxCardsToShowMobile?: number;
  maxCardsToShowTablet?: number;
  pcpId?: string;
}

export const NetworkAffiliationsSection: React.FC<
  NetworkAffiliationsSectionProps
> = ({
  affiliations,
  maxCardsToShow = 4,
  maxCardsToShowMobile = 3,
  maxCardsToShowTablet = 3,
  pcpId,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [calculatedMaxCards, setCalculatedMaxCards] = useState(1);
  const cardsContainerRef = useRef<HTMLDivElement>(null);

  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 1024px) and (min-width: 769px)');

  useEffect(() => {
    const calculateMaxCards = () => {
      if (isMobile || isTablet || !cardsContainerRef.current) {
        if (isMobile || isTablet) {
          setCalculatedMaxCards(maxCardsToShow);
        }
        return;
      }

      const containerWidth = cardsContainerRef.current.offsetWidth;
      const firstCard = cardsContainerRef.current.querySelector(
        '[data-testid^="network-affiliation-card-"]'
      ) as HTMLElement;

      const cardWidth = firstCard ? firstCard.offsetWidth : 220.8;
      const gap = 24;
      const cardsPossible = Math.floor(
        (containerWidth + gap) / (cardWidth + gap)
      );
      const cardsToShow = Math.min(Math.max(cardsPossible, 1), maxCardsToShow);

      if (cardsToShow !== calculatedMaxCards) {
        setCalculatedMaxCards(cardsToShow);
        if (isExpanded) {
          setIsExpanded(false);
        }
      }
    };

    calculateMaxCards();

    const timeoutId1 = setTimeout(calculateMaxCards, 0);
    const timeoutId2 = setTimeout(calculateMaxCards, 100);
    const timeoutId3 = setTimeout(calculateMaxCards, 300);

    let resizeObserver: ResizeObserver | null = null;
    if (cardsContainerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        calculateMaxCards();
      });
      resizeObserver.observe(cardsContainerRef.current);

      const firstCard = cardsContainerRef.current.querySelector(
        '[data-testid^="network-affiliation-card-"]'
      ) as HTMLElement;
      if (firstCard) {
        resizeObserver.observe(firstCard);
      }
    }

    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateMaxCards, 150);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      clearTimeout(resizeTimeout);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile, isTablet, maxCardsToShow, calculatedMaxCards, isExpanded]);

  const uiMessaging = useLagoon('ui-messaging')() || [];
  const infoMessageObj = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.NETWORK_AFFILIATIONS_INFO_ICON_MSG,
  });
  const infoMessage = infoMessageObj?.message || '';

  const sortedAffiliations = [...affiliations].sort((a, b) =>
    a.networkAffiliationName.localeCompare(b.networkAffiliationName)
  );

  const totalCount = sortedAffiliations.length;

  const getMaxCards = () => {
    if (isMobile) return maxCardsToShowMobile;
    if (isTablet) return maxCardsToShowTablet;
    return calculatedMaxCards;
  };

  const maxCards = getMaxCards();
  const cardsToDisplay = (
    isExpanded ? sortedAffiliations : sortedAffiliations.slice(0, maxCards)
  ).map((affiliation) => ({ ...affiliation, pcpId }));

  const infoPopoverContent = (
    <Text
      css={{
        width: '276px',
        fontFamily: 'sans-serif',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '140%',
        color: '#4B4D4F',
      }}
    >
      {infoMessage}
    </Text>
  );

  const shouldShowShowAllLink = totalCount > maxCards && !isExpanded;
  const shouldShowViewAllDetailsLink = totalCount > 1;

  const handleShowAllClick = () => {
    setIsExpanded(true);
  };

  const handleShowLessClick = () => {
    setIsExpanded(false);
  };

  if (!affiliations || affiliations.length === 0) {
    return null;
  }

  return (
    <>
      <Flex
        css={networkAffiliationsSectionContainer}
        data-auto-testid="network-affiliations-section"
        data-testid="network-affiliations-section"
        direction="column"
        id="network-affiliations-section"
      >
        <Flex css={networkAffiliationsHeaderContainer}>
          <Flex css={networkAffiliationsTitleContainer}>
            <Heading
              css={{
                ...h2SmallerFontForResponsive,
                ...networkAffiliationsTitleStyle,
              }}
              data-auto-testid="network-affiliations-title"
              data-testid="network-affiliations-title"
              display="h4"
              offset={1}
            >
              {t('Network affiliations')} ({totalCount})
            </Heading>
            <Popover
              align="start"
              aria-label={`${t('INFORMATION_ABOUT')} ${t(
                'Network affiliations'
              )}`}
              content={infoPopoverContent}
              css={{
                '.abyss-popover-close-button': {
                  width: '24px',
                  height: '24px',
                },
                '.abyss-popover-close-icon': {
                  color: '#4B4D4F',
                  width: '24px',
                  height: '24px',
                },
              }}
              data-auto-testid="network-affiliations-info-popover"
              data-testid="network-affiliations-info-popover"
              onOpenChange={() => setIsInfoOpen(!isInfoOpen)}
              open={isInfoOpen}
              width={300}
            >
              <IconMaterial
                css={{ cursor: 'pointer' }}
                data-auto-testid="network-affiliations-info-icon"
                data-testid="network-affiliations-info-icon"
                icon="info"
                onClick={() => setIsInfoOpen(true)}
                size={16}
                variant="outlined"
              />
            </Popover>
          </Flex>
          {shouldShowViewAllDetailsLink && (
            <LinkWithTracking
              after={
                <IconMaterial
                  css={networkAffiliationsIconStyle}
                  icon="chevron_right"
                  size="20px"
                />
              }
              analyticsInfo={{
                name: 'view all affiliation details',
                location: 'body:network-affiliations',
              }}
              css={networkAffiliationsViewAllDetailsLink}
              data-auto-testid="view-all-affiliation-details-link"
              data-testid="view-all-affiliation-details-link"
              isStandardAnchor
              onClick={(e) => e.preventDefault()}
            >
              {t('View all affiliation details')}
            </LinkWithTracking>
          )}
        </Flex>

        <div ref={cardsContainerRef}>
          <Flex
            css={
              isExpanded && !isMobile
                ? networkAffiliationsCardsContainer
                : networkAffiliationsCardsContainerCollapsed
            }
            data-testid="network-affiliations-cards-container"
          >
            {cardsToDisplay.map((affiliation, index) => (
              <NetworkAffiliationsCard
                affiliation={affiliation}
                key={`${affiliation.networkAffiliationId}-${index}`}
                testId={`network-affiliation-card-${index}`}
              />
            ))}
          </Flex>
        </div>

        {shouldShowShowAllLink && (
          <Flex css={networkAffiliationsLinkContainer}>
            <LinkWithTracking
              after={
                <IconMaterial
                  css={networkAffiliationsIconStyle}
                  icon="expand_more"
                  size="20px"
                />
              }
              analyticsInfo={{
                name: `show all ${totalCount}`,
                location: 'body:network-affiliations',
              }}
              css={networkAffiliationsLinkStyle}
              data-auto-testid="show-all-affiliations-link"
              data-testid="show-all-affiliations-link"
              isStandardAnchor
              onClick={handleShowAllClick}
            >
              {t('Show all')} ({totalCount})
            </LinkWithTracking>
          </Flex>
        )}
        {isExpanded && totalCount > maxCards && (
          <Flex css={networkAffiliationsLinkContainer}>
            <LinkWithTracking
              after={
                <IconMaterial
                  css={networkAffiliationsIconStyle}
                  icon="expand_less"
                  size="20px"
                />
              }
              analyticsInfo={{
                name: 'show less',
                location: 'body:network-affiliations',
              }}
              css={networkAffiliationsLinkStyle}
              data-auto-testid="show-less-affiliations-link"
              data-testid="show-less-affiliations-link"
              isStandardAnchor
              onClick={handleShowLessClick}
            >
              {t('Show less')}
            </LinkWithTracking>
          </Flex>
        )}
      </Flex>
    </>
  );
};
